import { useState, useEffect } from "react";
import { Lightbulb, RefreshCw, Star, TrendingUp, TrendingDown, Globe2 } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const TAG_STYLES = {
  TOP_PICK: { label: "TOP PICK LT", bg: "bg-emerald-500/20", border: "border-emerald-400/50", text: "text-emerald-300" },
  DEFENSIVE_GROWTH: { label: "DEFENSIVE GROWTH", bg: "bg-cyan-500/15", border: "border-cyan-400/40", text: "text-cyan-300" },
  TURNAROUND: { label: "TURNAROUND", bg: "bg-amber-500/15", border: "border-amber-400/40", text: "text-amber-300" },
  SPECULATIVE: { label: "SPÉCULATIF", bg: "bg-rose-500/10", border: "border-rose-400/30", text: "text-rose-300" },
};

function Stars({ n }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={10}
          className={i <= n ? "fill-yellow-400 text-yellow-400" : "text-slate-600"}
        />
      ))}
    </div>
  );
}

function IdeaRow({ h, owned, onTickerClick }) {
  const style = TAG_STYLES[h.tag] || TAG_STYLES.SPECULATIVE;
  const posChange = (h.change_pct || 0) >= 0;
  return (
    <div
      className="p-3 hover:bg-slate-800/40 transition cursor-pointer border-b border-slate-700/40 last:border-0"
      onClick={() => onTickerClick && onTickerClick(h.ticker)}
      data-testid={`lt-idea-${h.ticker}`}
    >
      <div className="flex items-start justify-between gap-3 mb-1.5">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-lg">{h.region}</span>
            <span className="text-white font-bold text-sm">{h.ticker}</span>
            <span className="text-slate-400 text-xs truncate">{h.name}</span>
            {owned && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 font-medium">
                HOLDING
              </span>
            )}
            <span className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${style.bg} ${style.border} ${style.text}`}>
              {style.label}
            </span>
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">{h.sector}</div>
        </div>
        <div className="text-right shrink-0">
          {h.current_price ? (
            <>
              <div className="text-white font-semibold text-sm">
                {h.ticker.includes(".L") || h.ticker.includes(".PA") || h.ticker.includes(".DE") || h.ticker.includes(".AS") || h.ticker.includes(".MI") || h.ticker.includes(".CO") || h.ticker.includes(".SW") ? "" : "$"}
                {h.current_price.toFixed(2)}
              </div>
              <div className={`text-[11px] flex items-center gap-0.5 justify-end ${posChange ? "text-emerald-400" : "text-rose-400"}`}>
                {posChange ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                {posChange ? "+" : ""}{(h.change_pct || 0).toFixed(2)}%
              </div>
            </>
          ) : (
            <div className="text-slate-500 text-xs">n/a</div>
          )}
          <div className="mt-0.5"><Stars n={h.rating || 0} /></div>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 leading-relaxed">{h.thesis}</p>
    </div>
  );
}

export default function LongTermIdeas({ onTickerClick, portfolioTickers = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [region, setRegion] = useState("europe");

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/curated/long-term-ideas`, { timeout: 25000 });
      setData(r.data);
    } catch (e) {
      console.error("LT ideas fetch error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const ownedSet = new Set((portfolioTickers || []).map((t) => (t || "").toUpperCase()));
  const list = data ? (region === "europe" ? data.europe : data.us) : [];

  return (
    <div className="glass-card rounded-xl border border-teal-500/20 overflow-hidden" data-testid="lt-ideas-panel">
      <div className="bg-gradient-to-r from-teal-600/15 via-emerald-600/10 to-transparent px-5 py-4 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-teal-500/20 border border-teal-400/30">
            <Lightbulb size={18} className="text-teal-300" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-base">Idées Long Terme 2026</h3>
            <p className="text-slate-400 text-xs">Sous-évaluées + marché en croissance structurelle</p>
          </div>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="p-2 rounded-lg bg-slate-700/60 hover:bg-slate-600 text-slate-300 transition disabled:opacity-50"
          data-testid="lt-ideas-refresh-btn"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="flex gap-1 p-2 border-b border-slate-700/60 bg-slate-900/40">
        <button
          onClick={() => setRegion("europe")}
          className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition flex items-center justify-center gap-1.5 ${
            region === "europe" ? "bg-teal-500/20 text-teal-200 border border-teal-400/40" : "text-slate-400 hover:bg-slate-800"
          }`}
          data-testid="lt-region-europe"
        >
          <Globe2 size={12} /> 🌍 Europe ({data?.europe?.length || 0})
        </button>
        <button
          onClick={() => setRegion("us")}
          className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition flex items-center justify-center gap-1.5 ${
            region === "us" ? "bg-teal-500/20 text-teal-200 border border-teal-400/40" : "text-slate-400 hover:bg-slate-800"
          }`}
          data-testid="lt-region-us"
        >
          🇺🇸 États-Unis ({data?.us?.length || 0})
        </button>
      </div>

      {loading && !data && <div className="p-6 text-center text-slate-400 text-sm">Chargement…</div>}

      {data && (
        <div>
          {list.map((h) => (
            <IdeaRow
              key={h.ticker}
              h={h}
              owned={ownedSet.has((h.ticker || "").toUpperCase())}
              onTickerClick={onTickerClick}
            />
          ))}
        </div>
      )}

      {data && data.note && (
        <div className="px-4 py-2 text-[11px] text-slate-500 bg-slate-800/30 border-t border-slate-700/60">
          {data.note}
        </div>
      )}
    </div>
  );
}
